import { useState } from "react";
import {
  ArchiveIcon,
  MessageSquareTextIcon,
  MoreHorizontalIcon,
  PlayIcon,
  RotateCcwIcon,
} from "lucide-react";
import type { CmmdBoardTask } from "@cmmd-forge/contracts";

import { Button } from "~/components/ui/button";
import { RESPONSIVE_ICON_BUTTON_CLASS, RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "~/components/ui/menu";
import { ProjectTaskArchiveDialog } from "./ProjectTaskArchiveDialog";
import { TaskWorkItemTypeIcon } from "./atoms/TaskWorkItemTypeIcon";
import { ProjectIdentityIcon } from "~/components/ProjectIdentityIcon";

export interface ProjectTaskWorkbenchHeaderProps {
  readonly task: CmmdBoardTask;
  readonly parentTask: CmmdBoardTask | null;
  readonly projectTitle: string;
  readonly projectIconUrl: string | null;
  readonly projectIcon: string | null;
  readonly title: string;
  readonly pending: boolean;
  readonly executing: boolean;
  readonly hasConversation: boolean;
  readonly onTitleChange: (title: string) => void;
  readonly onCommitTitle: () => void;
  readonly onOpenTask: (id: number) => void;
  readonly onStartWork: () => void;
  readonly onArchive: () => Promise<boolean>;
  readonly onUnarchive: () => void;
  /**
   * Host cannot write. Suppresses the write affordances rather than leaving
   * them clickable against a handler that refuses — a primary action that
   * silently does nothing reads as broken, not as read-only.
   */
  readonly readOnly?: boolean;
}

export function ProjectTaskWorkbenchHeader(props: ProjectTaskWorkbenchHeaderProps) {
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const parentTask = props.parentTask;

  const confirmArchive = async () => {
    await props.onArchive();
    setArchiveDialogOpen(false);
  };

  return (
    <header className="shrink-0 border-b border-border px-4 pt-[max(0.5rem,env(safe-area-inset-top))] pb-3 md:px-5 md:py-3 md:pr-14">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1.5 md:items-start md:gap-x-3 md:gap-y-2">
        <div
          aria-label={`Task ${props.task.id} location`}
          className="col-span-2 flex min-h-touch min-w-0 flex-wrap items-center gap-1.5 pr-12 text-sm text-muted-foreground md:min-h-0 md:pr-0"
        >
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <ProjectIdentityIcon
              iconUrl={props.projectIconUrl}
              icon={props.projectIcon}
              projectName={props.projectTitle}
              className="size-4"
            />
            <span className="truncate">{props.projectTitle}</span>
          </span>
          <span aria-hidden>/</span>
          {props.task.parentTaskId === null ? null : (
            <>
              {parentTask ? (
                <button
                  type="button"
                  aria-label={`Open parent task #${parentTask.id}`}
                  title={parentTask.title}
                  className="-mx-1 inline-flex min-h-8 shrink-0 items-center gap-1 rounded-sm px-1 font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => props.onOpenTask(parentTask.id)}
                >
                  <TaskWorkItemTypeIcon
                    workItemType={parentTask.workItemType}
                    className="[&_svg]:size-4"
                  />
                  <span>#{parentTask.id}</span>
                </button>
              ) : (
                <span
                  aria-label={`Parent task #${props.task.parentTaskId}`}
                  className="shrink-0 font-medium"
                >
                  #{props.task.parentTaskId}
                </span>
              )}
            </>
          )}
          <span className="inline-flex min-h-8 shrink-0 items-center gap-1 font-semibold text-foreground">
            {props.task.parentTaskId === null ? null : <span aria-hidden>/</span>}
            <TaskWorkItemTypeIcon
              workItemType={props.task.workItemType}
              className="[&_svg]:size-4"
            />
            <span>#{props.task.id}</span>
          </span>
        </div>
        <textarea
          aria-label="Task title"
          value={props.title}
          disabled={props.pending}
          readOnly={props.readOnly ?? false}
          rows={1}
          onChange={(event) => props.onTitleChange(event.currentTarget.value)}
          onBlur={props.onCommitTitle}
          className="col-span-2 field-sizing-content max-h-32 min-h-touch w-full resize-none bg-transparent py-1 text-xl font-semibold leading-snug tracking-tight text-foreground outline-none placeholder:text-muted-foreground md:col-span-1 md:col-start-1 md:row-start-2 md:min-h-9 md:py-0 md:text-2xl"
        />
        <div className="col-span-2 flex items-center justify-between gap-[8px] md:col-span-1 md:col-start-2 md:row-start-2 md:justify-end md:gap-2">
          {props.readOnly ? null : (
            <TaskWorkbenchWriteActions
              task={props.task}
              pending={props.pending}
              executing={props.executing}
              hasConversation={props.hasConversation}
              archiveDialogOpen={archiveDialogOpen}
              onArchiveDialogOpenChange={setArchiveDialogOpen}
              onConfirmArchive={confirmArchive}
              onStartWork={props.onStartWork}
              onUnarchive={props.onUnarchive}
            />
          )}
        </div>
      </div>
    </header>
  );
}

interface TaskWorkbenchWriteActionsProps {
  readonly task: CmmdBoardTask;
  readonly pending: boolean;
  readonly executing: boolean;
  readonly hasConversation: boolean;
  readonly archiveDialogOpen: boolean;
  readonly onArchiveDialogOpenChange: (open: boolean) => void;
  readonly onConfirmArchive: () => Promise<void>;
  readonly onStartWork: () => void;
  readonly onUnarchive: () => void;
}

/**
 * Start / open-conversation plus the archive menu.
 *
 * Extracted so the header keeps one job. Gating this cluster on `readOnly`
 * inline pushed the header past the complexity gate, and the branch reads
 * better as "render the write actions, or do not" than as another nested
 * ternary inside an already dense grid.
 */
function TaskWorkbenchWriteActions(props: TaskWorkbenchWriteActionsProps) {
  return (
    <>
      <Button
        type="button"
        size="sm"
        className={`${RESPONSIVE_TEXT_BUTTON_CLASS} min-w-0 px-3 md:flex-none`}
        disabled={
          props.pending || props.executing || (props.task.archived && !props.hasConversation)
        }
        onClick={props.onStartWork}
      >
        {props.hasConversation ? <MessageSquareTextIcon aria-hidden /> : <PlayIcon aria-hidden />}
        {props.executing ? "Starting…" : props.hasConversation ? "Open conversation" : "Start task"}
      </Button>
      <Menu>
        <MenuTrigger
          aria-label="More task actions"
          title="More task actions"
          render={
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              className={RESPONSIVE_ICON_BUTTON_CLASS}
            />
          }
        >
          <MoreHorizontalIcon aria-hidden />
        </MenuTrigger>
        <MenuPopup align="end" className="w-44">
          {props.task.archived ? (
            <MenuItem disabled={props.pending} onClick={props.onUnarchive}>
              <RotateCcwIcon aria-hidden />
              Restore task
            </MenuItem>
          ) : (
            <MenuItem
              variant="destructive"
              disabled={props.pending}
              onClick={() => props.onArchiveDialogOpenChange(true)}
            >
              <ArchiveIcon aria-hidden />
              Archive task
            </MenuItem>
          )}
        </MenuPopup>
      </Menu>
      <ProjectTaskArchiveDialog
        open={props.archiveDialogOpen}
        taskId={props.task.id}
        pending={props.pending}
        onOpenChange={props.onArchiveDialogOpenChange}
        onConfirm={props.onConfirmArchive}
      />
    </>
  );
}
